import React, { useState } from 'react';

interface AccountDeletionProps {
  appName?: string;
  onClose: () => void;
}

const AccountDeletion: React.FC<AccountDeletionProps> = ({ appName = 'Our Application', onClose }) => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-[310] size-14 bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all group"
      >
        <span className="material-symbols-outlined transform group-hover:rotate-90 transition-transform">close</span>
      </button>

      <div className="max-w-[700px] mx-auto px-6 py-32 text-white/80">
        <h1 className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-none mb-4 italic text-white flex flex-col gap-2">
          <span>Account</span>
          <span>Deletion</span>
        </h1>
        <p className="text-xl text-primary font-bold tracking-widest uppercase mb-12 border-b border-white/10 pb-6">
          {appName}
        </p>

        {submitted ? (
          <div className="bg-white/5 border border-primary/30 p-10 text-center space-y-6">
            <span className="material-symbols-outlined text-5xl text-primary block">check_circle</span>
            <h2 className="text-3xl font-bold text-white uppercase tracking-widest">Request Received</h2>
            <p className="text-lg font-light leading-relaxed text-white/60">
              We have received your account deletion request for <strong className="text-white">{email}</strong>. 
              Our team will process the permanent deletion of your account and all associated data within 30 days in accordance with data protection laws.
            </p>
            <p className="text-sm text-primary font-bold uppercase tracking-widest pt-4">You will receive a confirmation email shortly.</p>
          </div>
        ) : (
          <div className="space-y-12 font-light leading-relaxed text-lg">
            <section className="space-y-4">
              <p>
                We value your privacy. If you wish to delete your account and all associated data from <strong className="text-white">{appName}</strong>, 
                you can submit a deletion request below.
              </p>
              <div className="bg-white/5 border border-white/10 p-6 space-y-4 text-white/70">
                <h3 className="text-xl font-bold text-white uppercase tracking-widest mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">warning</span>
                  Important Information
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Deleting your account is a <strong className="text-white">permanent and irreversible action</strong>.</li>
                  <li>All your personal information, settings, and saved data will be securely erased.</li>
                  <li>Any AI-generated content (including stylised photos) stored in your account will be permanently deleted.</li>
                  <li>Active subscriptions must be cancelled separately through the Apple App Store or Google Play Store. Deleting your account does not cancel your subscriptions.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-6 uppercase tracking-widest">Submit Deletion Request</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-white/60 uppercase tracking-widest mb-2">
                    Account Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/20 text-white px-6 py-4 focus:outline-none focus:border-primary focus:bg-white/10 transition-all"
                    placeholder="Enter the email associated with your account"
                  />
                </div>
                
                <div>
                  <label htmlFor="reason" className="block text-sm font-bold text-white/60 uppercase tracking-widest mb-2">
                    Reason for deletion (Optional)
                  </label>
                  <textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                    className="w-full bg-white/5 border border-white/20 text-white px-6 py-4 focus:outline-none focus:border-primary focus:bg-white/10 transition-all resize-none"
                    placeholder="Help us improve by telling us why you're leaving..."
                  />
                </div>

                <div className="pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-6">
                  <p className="text-sm text-white/40">
                    By submitting this form, you verify that you are the owner of this account.
                  </p>
                  <button 
                    type="submit"
                    className="px-10 py-5 bg-red-600/20 text-red-500 border border-red-500/50 hover:bg-red-600 hover:text-white hover:border-red-600 font-black tracking-widest uppercase transition-all flex items-center gap-3"
                  >
                    <span>Request Deletion</span>
                    <span className="material-symbols-outlined text-lg">delete_forever</span>
                  </button>
                </div>
              </form>
            </section>

            <section className="pt-12 border-t border-white/10">
              <h2 className="text-xl font-bold text-white mb-4 uppercase tracking-widest">Alternative Contact</h2>
              <p>
                If you prefer not to use this form, or if you need assistance, you can request account deletion by emailing us directly at <a href="mailto:robthane@gmail.com" className="text-primary hover:underline font-bold">robthane@gmail.com</a>. Please send the request from the email address associated with your account and include "Account Deletion Request" in the subject line.
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDeletion;
